/*
 * Favorite store — persistence + the <store-favorite-toggle> control.
 *
 * A single favorite store per visitor, persisted in localStorage as
 * { handle, name, url } so the navbar (VIS-211) can render the store name and a
 * no-JS fallback link on any page without loading the locator.
 *
 * The persistence helpers are exported so other modules (the VIS-211
 * store-selector) can read/observe the favorite, along with favoriteStoreUrl /
 * favoriteAppointmentUrl, the single home for deriving the store's page and
 * appointment links from the handle when the stored URLs are empty (VIS-851:
 * store page unpublished at favorite time, or legacy entries). Every mutation dispatches a
 * `store-favorite:changed` event on `window`, keeping all mounted controls
 * (locator cards, detail hero, navbar) in sync within the document; the native
 * `storage` event covers cross-tab sync.
 *
 * For logged-in customers the favorite is also persisted on the account
 * (VIS-239) as a customer metafield, written through the app proxy declared by
 * the store-favorite-sync.liquid config element. Writes are fire-and-forget —
 * localStorage stays the source of truth for the current device, and
 * reconcile() repairs any divergence on the next page load. A second
 * localStorage key remembers the account value this device last confirmed, so
 * reconcile can tell which side moved since: the side that diverged from it is
 * the most recent action and wins. Without a confirmed value (guest pick
 * carried into sign-in, first sync), the device pick wins.
 *
 * The control is a custom element, so cards injected dynamically (e.g. into the
 * VIS-211 dialog) upgrade themselves once this module has loaded.
 */

const STORAGE_KEY = 'vo:favoriteStore';
const SYNCED_KEY = 'vo:favoriteStoreSynced';
// VIS-916: how long after a user pick reconcile() keeps trusting the local
// value over the account snapshot. Right after a pick, the next page can
// render before the metafield write is visible to liquid, so the snapshot
// looks stale/empty; without this window reconcile would wipe (or revert) the
// favorite that was chosen seconds earlier.
const RECENT_PICK_MS = 60 * 1000;
const CHANGE_EVENT = 'store-favorite:changed';
const STORE_PAGE_PATH = '/pages/centri-ottici/';
const APPOINTMENT_FORM_PATH = '/pages/richiesta-appuntamento?store=';

/**
 * `id` (the store's CPCO code) and `address` exist for the data layer
 * (VIS-921): the global "Prenota" CTA reports the favorite store with the same
 * store_id / store_city every other emitter sends.
 * @typedef {{ handle: string, name: string, url: string, appointmentUrl: string, id?: string, address?: string, pickedAt?: number }} Favorite
 */

/**
 * The current favorite store, or null.
 * @returns {Favorite | null}
 */
export function getFavorite() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.handle !== 'string' || !parsed.handle) return null;
    return {
      handle: parsed.handle,
      name: parsed.name || '',
      url: parsed.url || '',
      appointmentUrl: parsed.appointmentUrl || '',
      id: parsed.id || '',
      address: parsed.address || '',
      pickedAt: typeof parsed.pickedAt === 'number' ? parsed.pickedAt : 0,
    };
  } catch {
    return null;
  }
}

/**
 * Replace the favorite store (single-favorite: this overwrites any previous one).
 * @param {Favorite} favorite
 * @param {{ sync?: boolean }} [options] - sync: false skips the account write
 *   (used when applying the account's own value locally).
 */
export function setFavorite(favorite, { sync = true } = {}) {
  if (!favorite || !favorite.handle) return;
  const record = {
    handle: favorite.handle,
    name: favorite.name || '',
    url: favorite.url || '',
    appointmentUrl: favorite.appointmentUrl || '',
    id: favorite.id || '',
    address: favorite.address || '',
    // Stamped only on user picks: applying the account's own value back
    // (sync: false) must not count as a fresh pick, or reconcile() would keep
    // re-pushing it over later account-side changes.
    pickedAt: sync ? Date.now() : 0,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Private mode / quota — keep the in-memory dispatch so the current page
    // still reflects the choice for this session.
  }
  notify(record);
  if (sync) pushRemote(record.handle);
}

/**
 * Clear the favorite store.
 * @param {{ sync?: boolean }} [options]
 */
export function clearFavorite({ sync = true } = {}) {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  notify(null);
  if (sync) deleteRemote();
}

/**
 * @param {string} handle
 * @returns {boolean}
 */
export function isFavorite(handle) {
  if (!handle) return false;
  const favorite = getFavorite();
  return favorite != null && favorite.handle === handle;
}

/**
 * The favorite store's page URL: the stored one, or the metaobject page path
 * derived from the handle for favorites saved without a URL (store page
 * unpublished at the time, or legacy entries).
 * @param {Favorite | null} favorite
 * @returns {string} '' when there is no favorite
 */
export function favoriteStoreUrl(favorite) {
  if (!favorite || !favorite.handle) return '';
  return favorite.url || `${STORE_PAGE_PATH}${encodeURIComponent(favorite.handle)}`;
}

/**
 * The favorite store's appointment link: the stored scheduler URL when known,
 * or the manual booking form keyed by handle.
 * @param {Favorite | null} favorite
 * @returns {string} '' when there is no favorite
 */
export function favoriteAppointmentUrl(favorite) {
  if (!favorite || !favorite.handle) return '';
  return favorite.appointmentUrl || `${APPOINTMENT_FORM_PATH}${encodeURIComponent(favorite.handle)}`;
}

/** @param {Favorite | null} favorite */
function notify(favorite) {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { favorite } }));
}

/*
 * Account sync (VIS-239) — present only for logged-in customers, via the JSON
 * config element rendered by store-favorite-sync.liquid.
 */

/** @returns {{ endpoint: string, account: Favorite | null } | null} */
function syncConfig() {
  const element = document.querySelector('[data-store-favorite-sync-config]');
  if (!element) return null;
  try {
    const parsed = JSON.parse(element.textContent);
    return parsed && typeof parsed.endpoint === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The account handle this device last confirmed (pushed successfully or read
 * back at reconcile time), or null. Lets reconcile() tell an account-side
 * change/removal from a device pick that never reached the account.
 * @returns {string | null}
 */
function getSynced() {
  try {
    return localStorage.getItem(SYNCED_KEY) || null;
  } catch {
    return null;
  }
}

/** @param {string | null} handle */
function setSynced(handle) {
  try {
    if (handle) {
      localStorage.setItem(SYNCED_KEY, handle);
    } else {
      localStorage.removeItem(SYNCED_KEY);
    }
  } catch {
    // ignore — reconcile just falls back to device-wins next load
  }
}

/** Fire-and-forget metafield write; reconcile() repairs failures on next load. */
function pushRemote(handle) {
  const config = syncConfig();
  if (!config) return;
  fetch(config.endpoint, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handle }),
  })
    .then((response) => {
      if (response.ok) setSynced(handle);
    })
    .catch(() => {});
}

/** Fire-and-forget metafield clear. */
function deleteRemote() {
  const config = syncConfig();
  if (!config) return;
  fetch(config.endpoint, { method: 'DELETE' })
    .then((response) => {
      if (response.ok) setSynced(null);
    })
    .catch(() => {});
}

/*
 * Align localStorage and the account metafield on page load:
 * - guest (no config element): nothing to do;
 * - same handle on both sides: in sync, remember it as confirmed;
 * - sides differ: the side that moved away from the last confirmed value is
 *   the most recent action and wins — an account-side change or removal
 *   (admin, another device) updates this device, while a device pick that
 *   never reached the account (guest pick carried into sign-in, failed push)
 *   is pushed. A device-side clear whose DELETE failed is retried the same
 *   way. With no confirmed value and both sides set, the device wins, as
 *   before.
 */
function reconcile() {
  const config = syncConfig();
  if (!config) return;
  const local = getFavorite();
  const account = config.account && config.account.handle ? config.account : null;
  const synced = getSynced();

  if (local?.handle === account?.handle) {
    setSynced(account ? account.handle : null);
    return;
  }
  if (local && local.handle === synced) {
    // Account moved (changed or removed) after this device last confirmed it —
    // unless the local pick is fresh (VIS-916): the account snapshot is
    // rendered into the page and can predate a pick made seconds ago, and
    // trusting it here wiped the just-chosen favorite. Re-confirm the pick
    // instead; a genuine account-side change made within the window loses to
    // the re-push, and later loads converge on whichever side wrote last.
    if (local.pickedAt && Date.now() - local.pickedAt < RECENT_PICK_MS) {
      pushRemote(local.handle);
    } else if (account) {
      setFavorite(account, { sync: false });
      setSynced(account.handle);
    } else {
      clearFavorite({ sync: false });
      setSynced(null);
    }
  } else if (!local && account && account.handle === synced) {
    // Device cleared but the DELETE never landed — retry it.
    deleteRemote();
  } else if (local) {
    pushRemote(local.handle);
  } else if (account) {
    setFavorite(account, { sync: false });
    setSynced(account.handle);
  }
}

reconcile();

/**
 * <store-favorite-toggle> — wraps a checkbox toggle ([data-favorite-toggle]) and
 * a store identity in data-store-handle / data-store-name / data-store-url.
 * Checking it sets this store as the favorite (single-favorite: replaces any
 * previous one); unchecking clears it. Reflects the current favorite onto the
 * checkbox's `checked` state and stays in sync via the change event + the
 * cross-instance / cross-tab events.
 */
class StoreFavoriteToggle extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.storeHandle || '';
    this.storeName = this.dataset.storeName || '';
    this.url = this.dataset.storeUrl || '';
    this.appointmentUrl = this.dataset.storeAppointmentUrl || '';
    this.storeId = this.dataset.storeId || '';
    this.address = this.dataset.storeAddress || '';
    this.input = this.querySelector('[data-favorite-toggle]');

    this.#reflect();
    if (this.input) this.input.addEventListener('change', this.#onChange);
    window.addEventListener(CHANGE_EVENT, this.#reflect);
    window.addEventListener('storage', this.#onStorage);
  }

  disconnectedCallback() {
    if (this.input) this.input.removeEventListener('change', this.#onChange);
    window.removeEventListener(CHANGE_EVENT, this.#reflect);
    window.removeEventListener('storage', this.#onStorage);
  }

  #onChange = () => {
    if (this.input.checked) {
      setFavorite({
        handle: this.handle,
        name: this.storeName,
        url: this.url,
        appointmentUrl: this.appointmentUrl,
        id: this.storeId,
        address: this.address,
      });
    } else {
      clearFavorite();
    }
  };

  // localStorage `storage` events only fire in other tabs — mirror the change here.
  #onStorage = (event) => {
    if (event.key === STORAGE_KEY) this.#reflect();
  };

  // Programmatic `checked` assignment doesn't fire `change`, so there's no loop.
  #reflect = () => {
    if (this.input) this.input.checked = isFavorite(this.handle);
  };
}

if (!customElements.get('store-favorite-toggle')) {
  customElements.define('store-favorite-toggle', StoreFavoriteToggle);
}
